import { Images } from "../../utils/imageConstants";
import { useLocation, useNavigate } from "react-router-dom";
import { setActiveTab } from "../../redux/slice/activeTabSlice";
import { useDispatch } from "react-redux";
import { useState, useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getAuthToken,
  hasPremiumAccess,
  removeAuthToken,
} from "../../helper/helper";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { setGuestMode } from "../../redux/slice/authModalSlice";
import { clearGuestMode } from "../../helper/helper";
import { openAuth, closeAuth } from "../../redux/slice/authModalSlice";
import { Api } from "../../services/interceptor";
import { QUERY_KEY } from "../../services/queryEndPoint";
import useDebounce from "../../hooks/useDebounce";
import { setSelectedLeague } from "../../redux/slice/selectedLeagueSlice";
import { resetUser } from "../../redux/slice/playerProfileSlice";
import user from "../../assets/images/user.svg";
import {
  extractGlobalSearchItems,
  buildMinimalLeagueFromWebSearch,
  type GlobalSearchItem,
} from "../../utils/websearchParse";

// Helper function to decode JWT and get user initial
const getUserInitial = (token: string | undefined): string => {
  if (!token) return "";
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join(""),
    );
    const decoded = JSON.parse(jsonPayload);
    const email =
      decoded.email || decoded.username || decoded["cognito:username"] || "";
    if (email) {
      return email.charAt(0).toUpperCase();
    }
    return "";
  } catch (error) {
    console.error("Error decoding token:", error);
    return "";
  }
};

const TopHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const appDispatch = useAppDispatch();
  const token = getAuthToken();
  const isLoggedIn = !!token;
  const [canAccessStatGpt, setCanAccessStatGpt] = useState(
    () => isLoggedIn && hasPremiumAccess(token),
  );
  const userInitial = getUserInitial(token);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showSearchPopover, setShowSearchPopover] = useState(false);
  const headerContentRef = useRef<HTMLDivElement>(null);
  const desktopProfileDropdownRef = useRef<HTMLDivElement>(null);
  const mobileProfileDropdownRef = useRef<HTMLDivElement>(null);
  const companyDropdownRef = useRef<HTMLDivElement>(null);
  const searchPopoverRef = useRef<HTMLDivElement>(null);
  const mobileSearchPopoverRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const debouncedSearchTerm = useDebounce(searchTerm, 350);
  const userProfile = useAppSelector((s) => s.selectedProfile.playerProfile);
  const { isLoading: isSearching, data: searchResults = [] } = useQuery<
    GlobalSearchItem[]
  >({
    queryKey: [QUERY_KEY.websearch, debouncedSearchTerm],
    enabled: showSearchPopover && debouncedSearchTerm.trim().length > 0,
    queryFn: async () => {
      const response = await Api.get(QUERY_KEY.websearch, {
        params: { name: debouncedSearchTerm.trim() },
      });
      return extractGlobalSearchItems(response?.data);
    },
  });

  const openSearchPopover = () => {
    setShowSearchPopover(true);
  };

  const closeSearchPopover = () => {
    setShowSearchPopover(false);
    setSearchTerm("");
  };

  const handleSearchSelect = (item: GlobalSearchItem) => {
    const leagueId = item.leagueId ?? (item.type === "league" ? item.id : undefined);
    const leagueName = item.leagueName ?? (item.type === "league" ? item.title : undefined);

    if (leagueId && leagueName) {
      appDispatch(setSelectedLeague(buildMinimalLeagueFromWebSearch(leagueId, leagueName)));
    }

    if (item.type === "league" && leagueId && leagueName) {
      dispatch(setActiveTab("home"));
      navigate(`/my-leagues/${leagueId}`);
      closeSearchPopover();
      return;
    }

    if (item.type === "tournament" && item.tournamentId) {
      sessionStorage.setItem("tournamentId", item.tournamentId);
      sessionStorage.setItem(
        "tournamentName",
        item.tournamentName ?? item.title,
      );
      if (leagueId) {
        navigate(`/my-leagues/${leagueId}/tournaments/${item.tournamentId}`);
      } else {
        navigate("/tournaments/tournaments-details");
      }
      closeSearchPopover();
      return;
    }

    if (item.type === "team") {
      if (item.teamId) {
        navigate(`/my-team/${item.teamId}`);
      } else if (item.teamSnapshotId || item.tournamentId) {
        sessionStorage.setItem("teamId", item.teamId ?? "");
        sessionStorage.setItem("teamSnapshotId", item.teamSnapshotId ?? "");
        sessionStorage.setItem("tournamentId", item.tournamentId ?? "");
        navigate("/tournaments/team-info");
      }
      closeSearchPopover();
      return;
    }

    if (item.type === "player") {
      const pid = item.playerId ?? item.id;
      if (!pid) {
        closeSearchPopover();
        return;
      }
      if (item.teamId) sessionStorage.setItem("teamId", item.teamId);
      if (item.tournamentId) sessionStorage.setItem("tournamentId", item.tournamentId);
      sessionStorage.setItem("playerId", pid);
      // Query param forces route update when already on player profile; sessionStorage alone can miss re-renders
      navigate(`/tournaments/player-profile?playerId=${encodeURIComponent(pid)}`);
      closeSearchPopover();
      return;
    }
  };

  // Handle logout
  const handleLogout = () => {
    removeAuthToken();
    clearGuestMode();
    appDispatch(setGuestMode(false));
    appDispatch(closeAuth());
    appDispatch(resetUser());
    setShowProfileDropdown(false);
    navigate("/", { replace: true });
  };

  // handle streamer dashboard routing
  const goToStreamerDashboard = () => {
    setShowProfileDropdown(false);
    setIsMobileMenuOpen(false);
    navigate("/streamer-dashboard", { replace: true });
  }

  // Handle Login click
  const handleLoginClick = () => {
    navigate("/", { replace: true });
    appDispatch(openAuth("signin"));
  };

  const handleLogoClick = () => {
    navigate("/");
    dispatch(setActiveTab("home"));
    sessionStorage.setItem("activeTab", "home");
    setIsMobileMenuOpen(false);
  };

  const handleNavigate = (route: string) => {
    navigate(route);
    setIsMobileMenuOpen(false);
  };

  const handleHomeClick = () => {
    dispatch(setActiveTab("home"));
    handleNavigate(isLoggedIn ? "/" : "/home");
  };

  const isRouteActive = (
    route:
      | "home"
      | "my-league"
      | "my-team"
      | "pricing"
      | "about"
      | "statgpt",
  ) => {
    if (route === "home") {
      return (
        location.pathname === "/home" ||
        (location.pathname === "/" && isLoggedIn)
      );
    }
    if (route === "my-league") {
      return (
        location.pathname.startsWith("/my-league") ||
        location.pathname.startsWith("/my-leagues")
      );
    }
    if (route === "my-team") {
      return location.pathname.startsWith("/my-team");
    }
    if (route === "pricing") {
      return location.pathname.startsWith("/pricing");
    }
    if (route === "about") {
      return location.pathname.startsWith("/about");
    }
    return location.pathname.startsWith("/stat-gpt");
  };

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const clickTarget = event.target as Node;
      const isInsideDesktopDropdown =
        desktopProfileDropdownRef.current?.contains(clickTarget);
      const isInsideMobileDropdown =
        mobileProfileDropdownRef.current?.contains(clickTarget);
      const isInsideCompanyDropdown =
        companyDropdownRef.current?.contains(clickTarget);
      const isInsideSearchPopover =
        searchPopoverRef.current?.contains(clickTarget) ||
        mobileSearchPopoverRef.current?.contains(clickTarget);
      const isInsideHeader = headerContentRef.current?.contains(clickTarget);

      if (!isInsideDesktopDropdown && !isInsideMobileDropdown) {
        setShowProfileDropdown(false);
      }

      if (!isInsideCompanyDropdown) {
        setShowCompanyDropdown(false);
      }

      if (!isInsideSearchPopover) {
        setShowSearchPopover(false);
      }

      if (!isInsideHeader) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!showSearchPopover) return;
    searchInputRef.current?.focus();
  }, [showSearchPopover]);

  useEffect(() => {
    const refreshPremiumAccess = () => {
      const latestToken = getAuthToken();
      setCanAccessStatGpt(!!latestToken && hasPremiumAccess(latestToken));
    };

    refreshPremiumAccess();
    window.addEventListener("premium-access-updated", refreshPremiumAccess);
    window.addEventListener("storage", refreshPremiumAccess);
    return () => {
      window.removeEventListener(
        "premium-access-updated",
        refreshPremiumAccess,
      );
      window.removeEventListener("storage", refreshPremiumAccess);
    };
  }, [location.pathname]);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <header className="top-header">
      <div className="header-container">
        <div className="header-content" ref={headerContentRef}>
          <div className="mobile-header-content">
            <button
              className="mobile-icon-btn"
              type="button"
              aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={isMobileMenuOpen}
              onClick={() => {
                closeSearchPopover();
                setIsMobileMenuOpen((prev) => !prev);
              }}
            >
              <span className="mobile-hamburger" aria-hidden="true">
                <span></span>
                <span></span>
                <span></span>
              </span>
            </button>

            <button
              className="mobile-brand-button"
              type="button"
              onClick={handleLogoClick}
            >
              <img
                src={Images.main_logo}
                alt="CricCenter logo"
                className="mobile-brand-logo"
              />
              <span className="mobile-brand-text">CricCenter</span>
            </button>

            <div
              className="mobile-header-actions"
              ref={mobileProfileDropdownRef}
            >
              <div className="mobile-search-wrap" ref={mobileSearchPopoverRef}>
                <button
                  className="mobile-icon-btn"
                  type="button"
                  aria-label={showSearchPopover ? "Close search" : "Search"}
                  aria-expanded={showSearchPopover}
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    if (showSearchPopover) {
                      closeSearchPopover();
                    } else {
                      openSearchPopover();
                    }
                  }}
                >
                  <img
                    src={Images.search_icon}
                    alt=""
                    className="mobile-search-icon"
                  />
                </button>

                {showSearchPopover && (
                  <div
                    className="header-search-popover mobile-search-popover"
                    role="dialog"
                    aria-label="Global search"
                  >
                    <div className="header-search-input-wrap">
                      <input
                        ref={searchInputRef}
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search leagues, tournaments, teams, players..."
                      />
                      <img src={Images.search_icon_input} alt="Search" />
                    </div>
                    <div className="header-search-results">
                      {debouncedSearchTerm.trim().length === 0 ? (
                        <div className="header-search-empty">
                          Search anything on CricCenter
                        </div>
                      ) : isSearching ? (
                        <div className="header-search-empty">Searching...</div>
                      ) : searchResults.length > 0 ? (
                        searchResults.map((item) => (
                          <button
                            key={`mobile-${item.type}-${item.id}`}
                            type="button"
                            className="header-search-result-item"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSearchSelect(item);
                            }}
                          >
                            <span className="header-search-result-top">
                              <span className="header-search-result-title">
                                {item.title}
                              </span>
                              <span
                                className={`header-search-result-type type-${item.type}`}
                              >
                                {item.type}
                              </span>
                            </span>
                            {item.subtitle && (
                              <span className="header-search-result-subtitle">
                                {item.subtitle}
                              </span>
                            )}
                          </button>
                        ))
                      ) : (
                        <div className="header-search-empty">
                          No results found
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <button
                className="mobile-icon-btn"
                type="button"
                aria-label="Notifications"
              >
                <img
                  src={Images.notification_icon}
                  alt=""
                  className="mobile-notification-icon"
                />
              </button>

              {isLoggedIn ? (
                <button
                  className="mobile-avatar-initial-btn"
                  type="button"
                  aria-label="Profile"
                  onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                >
                  <span>{userInitial || "U"}</span>
                </button>
              ) : (
                <button
                  className="mobile-avatar-btn"
                  type="button"
                  aria-label="Sign in"
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    handleLoginClick();
                  }}
                >
                  <img
                    src={Images.dummy_profile}
                    alt=""
                    className="mobile-avatar"
                  />
                </button>
              )}

              {isLoggedIn && showProfileDropdown && (
                <div className="profile-dropdown-menu">
                  <button
                    onClick={() => {
                      setShowProfileDropdown(false);
                      navigate("/profile");
                    }}
                    className="profile-dropdown-button"
                  >
                    Profile
                  </button>
                  {userProfile?.streamerProfile != null && (<button
                    onClick={goToStreamerDashboard}
                    className="profile-dropdown-button"
                  >
                    Streamer Dashboard
                  </button>)}
                  <button
                    onClick={handleLogout}
                    className="profile-dropdown-button"
                  >
                    Logout
                  </button>


                </div>
              )}
            </div>
          </div>
          {isMobileMenuOpen && (
            <nav className="mobile-nav-panel" aria-label="Mobile navigation">
              <button
                className={`mobile-nav-link ${isRouteActive("home") ? "is-active" : ""}`}
                type="button"
                onClick={handleHomeClick}
              >
                Home
              </button>
              <button
                className={`mobile-nav-link ${isRouteActive("my-league") ? "is-active" : ""}`}
                type="button"
                onClick={() => handleNavigate("/my-leagues")}
              >
                My League
              </button>
              <button
                className={`mobile-nav-link ${isRouteActive("my-team") ? "is-active" : ""}`}
                type="button"
                onClick={() => handleNavigate("/my-team")}
              >
                My Team
              </button>
              <button
                className={`mobile-nav-link ${isRouteActive("pricing") ? "is-active" : ""}`}
                type="button"
                onClick={() => handleNavigate("/pricing")}
              >
                Pricing
              </button>
              <button
                className={`mobile-nav-link ${isRouteActive("about") ? "is-active" : ""}`}
                type="button"
                onClick={() => handleNavigate("/about")}
              >
                About Us
              </button>

              {canAccessStatGpt && (
                <button
                  className={`mobile-nav-link ${isRouteActive("statgpt") ? "is-active" : ""}`}
                  type="button"
                  onClick={() => handleNavigate("/stat-gpt")}
                >
                  Statgpt
                </button>
              )}
            </nav>
          )}

          {/* Left: Logo + Brand Name */}
          <div className="brand-section">
            <div className="logo-wrapper">
              <img
                src={Images.main_logo}
                alt="Cric Center Logo"
                className="main-logo"
                onClick={handleLogoClick}
              />
            </div>
            <h1 className="brand-name">CricCenter</h1>
          </div>
          <div className="vertical-separator"></div>

          {/* Middle: Navigation Links */}
          <nav className="nav-links">
            <button
              className={`nav-link nav-link-button ${isRouteActive("home") ? "is-active" : ""}`}
              type="button"
              onClick={handleHomeClick}
            >
              Home
            </button>

            <button
              className={`nav-link nav-link-button ${isRouteActive("my-league") ? "is-active" : ""}`}
              type="button"
              onClick={() => handleNavigate("/my-leagues")}
            >
              My league
            </button>
            <button
              className={`nav-link nav-link-button ${isRouteActive("my-team") ? "is-active" : ""}`}
              type="button"
              onClick={() => handleNavigate("/my-team")}
            >
              My Team
            </button>
            <button
              className={`nav-link nav-link-button ${isRouteActive("pricing") ? "is-active" : ""}`}
              type="button"
              onClick={() => handleNavigate("/pricing")}
            >
              Pricing
            </button>
            <div ref={companyDropdownRef} className="company-dropdown-wrapper">
              <button
                className={`nav-link nav-link-button ${isRouteActive("about") ? "is-active" : ""} ${showCompanyDropdown ? "is-open" : ""}`}
                type="button"
                onClick={() => setShowCompanyDropdown((prev) => !prev)}
                aria-expanded={showCompanyDropdown}
                aria-haspopup="true"
              >
                Company
                <img
                  src={Images.subheader_dropdown}
                  alt=""
                  className="company-dropdown-caret"
                />
              </button>
              {showCompanyDropdown && (
                <div className="company-dropdown-menu">
                  <button
                    type="button"
                    className={`company-dropdown-item ${isRouteActive("about") ? "is-active" : ""}`}
                    onClick={() => {
                      handleNavigate("/about");
                      setShowCompanyDropdown(false);
                    }}
                  >
                    About Us
                  </button>
                </div>
              )}
            </div>
          </nav>

          {/* Right: Search + Sign In (Before Login) */}
          {!isLoggedIn ? (
            <div className="header-right">
              <div className="header-global-search" ref={searchPopoverRef}>
                <div
                  className={`search-section ${showSearchPopover ? "is-open" : ""}`}
                  role="button"
                  tabIndex={0}
                  onClick={openSearchPopover}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      openSearchPopover();
                    }
                  }}
                >
                  <span className="search-text">Search</span>
                  <button aria-label="Search" className="search-icon-button" type="button">
                    <img src={Images.search_icon} alt="Search" />
                  </button>
                </div>

                {showSearchPopover && (
                  <div
                    className="header-search-popover"
                    role="dialog"
                    aria-label="Global search"
                  >
                    <div className="header-search-input-wrap">
                      <input
                        ref={searchInputRef}
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search leagues, tournaments, teams, players..."
                      />
                      <img src={Images.search_icon_input} alt="Search" />
                    </div>
                    <div className="header-search-results">
                      {debouncedSearchTerm.trim().length === 0 ? (
                        <div className="header-search-empty">
                          Search anything on CricCenter
                        </div>
                      ) : isSearching ? (
                        <div className="header-search-empty">Searching...</div>
                      ) : searchResults.length > 0 ? (
                        searchResults.map((item) => (
                          <button
                            key={`${item.type}-${item.id}`}
                            type="button"
                            className="header-search-result-item"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSearchSelect(item);
                            }}
                          >
                            <span className="header-search-result-top">
                              <span className="header-search-result-title">
                                {item.title}
                              </span>
                              <span
                                className={`header-search-result-type type-${item.type}`}
                              >
                                {item.type}
                              </span>
                            </span>
                            {item.subtitle && (
                              <span className="header-search-result-subtitle">
                                {item.subtitle}
                              </span>
                            )}
                          </button>
                        ))
                      ) : (
                        <div className="header-search-empty">
                          No results found
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className="vertical-separator"></div>
              <button
                onClick={handleLoginClick}
                className="signin-section"
              >
                <span className="signin-text">Sign In</span>
                <img src={user} alt="Sign in" className="signin-icon" />
              </button>
            </div>
          ) : (
            /* After Login: New Design with StatGPT and User Initial */
            <div className="header-right">
              {canAccessStatGpt && (
                <button
                  className="statgpt-button"
                  onClick={() => navigate("/stat-gpt")}
                >
                  <div className="statgpt-button-content">
                    <img
                      src={Images.main_logo}
                      alt="StatGPT"
                      className="statgpt-icon"
                    />
                    <span className="statgpt-text">Statgpt</span>
                  </div>
                </button>
              )}

              {/* Search Section */}
              <div className="header-global-search" ref={searchPopoverRef}>
                <div
                  className={`search-section ${showSearchPopover ? "is-open" : ""}`}
                  role="button"
                  tabIndex={0}
                  onClick={openSearchPopover}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      openSearchPopover();
                    }
                  }}
                >
                  <span className="search-text">Search</span>
                  <button aria-label="Search" className="search-icon-button" type="button">
                    <img src={Images.search_icon} alt="Search" />
                  </button>
                </div>

                {showSearchPopover && (
                  <div
                    className="header-search-popover"
                    role="dialog"
                    aria-label="Global search"
                  >
                    <div className="header-search-input-wrap">
                      <input
                        ref={searchInputRef}
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search leagues, tournaments, teams, players..."
                      />
                      <img src={Images.search_icon_input} alt="Search" />
                    </div>
                    <div className="header-search-results">
                      {debouncedSearchTerm.trim().length === 0 ? (
                        <div className="header-search-empty">
                          Search anything on CricCenter
                        </div>
                      ) : isSearching ? (
                        <div className="header-search-empty">Searching...</div>
                      ) : searchResults.length > 0 ? (
                        searchResults.map((item) => (
                          <button
                            key={`${item.type}-${item.id}`}
                            type="button"
                            className="header-search-result-item"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSearchSelect(item);
                            }}
                          >
                            <span className="header-search-result-top">
                              <span className="header-search-result-title">
                                {item.title}
                              </span>
                              <span
                                className={`header-search-result-type type-${item.type}`}
                              >
                                {item.type}
                              </span>
                            </span>
                            {item.subtitle && (
                              <span className="header-search-result-subtitle">
                                {item.subtitle}
                              </span>
                            )}
                          </button>
                        ))
                      ) : (
                        <div className="header-search-empty">
                          No results found
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Vertical Separator */}
              <div className="vertical-separator"></div>

              {/* User Initial Circle */}
              <div
                ref={desktopProfileDropdownRef}
                className="user-initial-wrapper"
              >
                <button
                  className="user-initial-button"
                  onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                >
                  <span className="user-initial">{userInitial || "U"}</span>
                </button>
                {showProfileDropdown && (
                  <div className="profile-dropdown-menu">
                    <button
                      onClick={() => {
                        setShowProfileDropdown(false);
                        navigate("/profile");
                      }}
                      className="profile-dropdown-button"
                    >
                      Profile
                    </button>
                    {userProfile?.streamerProfile != null && (<button
                      onClick={goToStreamerDashboard}
                      className="profile-dropdown-button"
                    >
                      Streamer Dashboard
                    </button>)}
                    <button
                      onClick={handleLogout}
                      className="profile-dropdown-button"
                    >
                      Logout
                    </button>

                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default TopHeader;
